// Libs
import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

// Components
import "@collectivehealth/dls-icon-label";
import "@collectivehealth/dls-icons/dist/es/elements/dls-icon-external-link-small";
import { NavLink, matchPath } from "react-router-dom";
import MenuIcon from "components/sidebar/MenuIcon";
import RenderIf from "components/RenderIf";

// Styles
import styles from "./MenuLink.css";

// Constants
import DATA_HOOKS from "./MenuLink.datahooks";

export default class MenuLink extends Component {
  static propTypes = {
    // If hasSubMenu, show chevron and show submenu on click;
    // else, chevron is hidden and route to another page on click
    hasSubMenu: PropTypes.bool.isRequired,
    isDisplayed: PropTypes.bool.isRequired,
    // If isSubMenuLink, apply secondary style to menu items; else, apply primary style
    isSubMenuLink: PropTypes.bool.isRequired,
    path: PropTypes.string.isRequired,
    // If you want the path to navigate to be specific but match a more general basePath
    basePath: PropTypes.string,
    onClick: PropTypes.func,
    persistQueryParams: PropTypes.bool,
    search: PropTypes.string,
    title: PropTypes.string.isRequired,

    /**
     * Flag which indicate whether a `New` label should be rendered next to menu title
     */
    hasNewLabel: PropTypes.bool,

    /**
     * Flag indicates whether an export icon should be rendered next to menu title
     */
    hasExternalLinkIcon: PropTypes.bool
  };

  static defaultProps = {
    hasNewLabel: false,
    hasSubMenu: false,
    isDisplayed: true,
    isSubMenuLink: false
  };

  render() {
    const {
      hasSubMenu,
      hasNewLabel,
      hasExternalLinkIcon,
      isDisplayed,
      isSubMenuLink,
      onClick,
      path,
      basePath = null,
      persistQueryParams,
      search,
      title,
      subtitle
    } = this.props;

    return (
      <NavLink
        activeClassName={styles.active}
        className={classnames(styles.root, {
          [styles.primary]: !isSubMenuLink,
          [styles.secondary]: isSubMenuLink,
          [styles.hidden]: !isDisplayed
        })}
        data-hook={isSubMenuLink ? DATA_HOOKS.SUBMENU_LINK : DATA_HOOKS.MENU_LINK}
        onClick={onClick}
        to={location => ({
          ...location,
          pathname: path,
          search: persistQueryParams ? location.search : search
        })}
        tabIndex={isDisplayed ? 0 : -1}
        isActive={(match, location) => {
          // this allows us to match to a base (vs. exact) path
          // (e.g. /claims/medical matches /claims/medical/clinical and /claims/medical/service)
          // while still having the link point to an exact path (e.g. /claims/medical/service)
          if (match) {
            return true;
          } else if (
            basePath &&
            matchPath(location.pathname, { path: basePath, exact: false, strict: true })
          ) {
            return true;
          }
          return false;
        }}
      >
        <div className={styles.layout}>
          <div className={styles.indicator} />
          {this.renderTitle(title, subtitle, isSubMenuLink, hasNewLabel)}
        </div>
        {this.renderChevron(hasSubMenu)}
        {this.renderExternalLinkIcon(hasExternalLinkIcon)}
      </NavLink>
    );
  }

  // RENDERERS

  /**
   * Renders the chevron if the menu link has a submenu.
   * @param {boolean} hasSubMenu
   * @return {(Component|null)}
   */
  renderChevron(hasSubMenu) {
    return (
      <RenderIf test={hasSubMenu}>
        <div className={styles.chevron}>
          <dls-icon-chevron-right />
        </div>
      </RenderIf>
    );
  }

  /**
   * Renders the external link icon if specified
   * @param hasExternalLinkIcon {boolean} render indicator
   * @returns {JSX.Element}
   */
  renderExternalLinkIcon(hasExternalLinkIcon) {
    return hasExternalLinkIcon ? (
      <div className={styles.chevron}>
        <dls-icon-external-link-small />
      </div>
    ) : (
      <React.Fragment />
    );
  }

  /**
   * Renders the link title (includes title and icon).
   * @param {string} title Title to be rendered.
   * @param {string} subtitle Subtitle to be rendered. If not provided, title will be used instead. Optional.
   * @param {boolean} isSubMenuLink Boolean if MenuLink is a part of SubMenu.
   * @param {boolean} hasNewLabel render label 'New' if this flag is set to true
   * @return {Component}
   */
  renderTitle(title, subtitle, isSubMenuLink, hasNewLabel) {
    const titleText = subtitle ? subtitle : title;
    return (
      <div className={styles.title}>
        <dls-icon-label space="medium">
          {!isSubMenuLink ? <MenuIcon title={title} /> : null}
          <span className={styles.titleText} slot="icon-label-text">
            {hasNewLabel ? this.renderTitleWithStatus(titleText) : titleText}
          </span>
        </dls-icon-label>
      </div>
    );
  }

  /**
   * Renders menu item title text and label 'New'
   * @param title {string} the menu item title text
   * @returns {JSX.Element}
   */
  renderTitleWithStatus(title) {
    return (
      <React.Fragment>
        <span>{title}</span>
        <span className={styles.statusLabel}>New</span>
      </React.Fragment>
    );
  }
}
